/* import __COLOCATED_TEMPLATE__ from './transfer-method.hbs'; */
import Component from '@ember/component';
import { observer } from '@ember/object';
import { on } from '@ember/object/evented';
import { showModal } from "sh-www/utils/modal-controls";
import { PAYOUT_METHOD } from "sh-www/models/payout-method";
import { computed } from '@ember/object';
import { task } from "ember-concurrency";
import { inject as service } from '@ember/service';
import { htmlSafe } from '@ember/template';

const CLASS_NAME = 'SH-c-payout-add-method-transfer-method';
const CONTAINER_ID = 'hyperwallet-drop-in--transfer-method';

/**
 * The sets of countries that need special configuration. This information is not mentioned in the
 * online Hyperwallet documentation, but you can read more about it in the PDF sent to us from HW
 * support team titled "Storyhunter_HW - Drop-in UI Proposed Initialisation Logic.pdf", which can be
 * found in Confluence here: https://storyhunter-dev.atlassian.net/l/c/pJcCmZKD
 */
const SPECIAL_COUNTRIES = {
  USE_PROFILE: ['CN', 'JP', 'RU'],
};

const BANK_INSTRUCTION_COUNTRIES = [
  'CO', // Colombia
  'TR', // Turkey
];

export default Component.extend({
  classNames: [CLASS_NAME, 'LSG-l-grid', 'justify-center'],
  CLASS_NAME,
  CONTAINER_ID,

  api: service(),
  errorTracker: service('error-tracker'),
  shData: service(),

  /** @type {A<Country>|null} */
  countries: null,

  /** @param {HyperwalletUser|null} */
  hyperwalletUser: null,

  /** @param {PayoutMethod|null} */
  payoutMethod: null,

  /** @param {String} */
  scriptHost: '',

  /** @param {Function} */
  getAuthToken: () => {},

  /** @param {Function} */
  onSubmitSuccess: () => {},

  /** @type {Boolean} */
  isLoading: true,

  /** @type {Boolean} */
  isTransitioning: false,

  /** @type {Function} */
  removeScript: () => {},

  /** @type {Boolean} */
  isScriptRemoved: false,

  bankInstructions: computed(
    'payoutMethod.{country,currency}',
    'countries.[]',
    function () {
      const countries = this.countries;
      if (!countries) { return null; }

      const country = countries.findBy('code', this.payoutMethod.country);
      if (!country) { return null; }

      const receiptCurrency = this.payoutMethod.currency;

      return htmlSafe(`Some accounts in ${country.name} provide different IBAN numbers to receive funds in either USD or ${receiptCurrency}. <b>Please provide the IBAN number that allows for receipt of ${receiptCurrency}.</b>`);
    }
  ),

  showBankInstructions: computed(
    'payoutMethod.{accountMethod,country}',
    function () {
      return BANK_INSTRUCTION_COUNTRIES.includes(this.get('payoutMethod.country'))
        && this.get('payoutMethod.accountMethod') === PAYOUT_METHOD.HYPERWALLET_ACCOUNT_METHODS.BANK;
    }
  ),

  loadCountriesObserver: on('init', observer('showBankInstructions', function() {
    if (this.showBankInstructions && !this.countries) {
      this.loadCountries.perform();
    }
  })),

  loadCountries: task(function *() {
    try {
      const response = yield this.get('api').request('/countries');

      this.shData.pushResponseToStore(response);
      const countries = this.shData.getModelArrayFromResponse(response, 'country');
      this.set('countries', countries);
    } catch (error) {
      // Just handle silently
      this.get('errorTracker').error(error, this);

      this.set('countries', null);
    }
  }).drop(),

  didInsertElement() {
    this._super(...arguments);

    const retrieveAuthenticationToken = (callback) => {
      this.get('getAuthToken')().then((authToken) => {
        callback(authToken);
      });
    };

    const configureWidget = () => {
      let widget;

      const hyperwalletUser = this.get('hyperwalletUser');
      const payoutMethod = this.get('payoutMethod');
      const country = payoutMethod.get('country');

      const options = {
        el: document.getElementById(CONTAINER_ID),
        transferMethodConfiguration: {
          country,
          currency: payoutMethod.get('currency'),
          type: payoutMethod.get('accountMethod'),
          profileType: hyperwalletUser.get('profileType'),
        },
        onLoading: (isLoading) => {
          if (this.get('isScriptRemoved')) return;
          if (isLoading) return;

          this.set('isLoading', false);
        },
        onBeforeSubmit: () => {
          if (this.get('isScriptRemoved')) return;

          widget.disable();
        },
        onComplete: (transferMethod) => {
          if (this.get('isScriptRemoved')) return;

          this.set('isTransitioning', true);
          this.get('onSubmitSuccess')(transferMethod);
        },
      };

      /**
       * ***** NOTE *****
       * We used to set options.skipProfileFields to true for everything not in a
       * SPECIAL_COUNTRIES.USE_PROFILE_ADD_DATA array of exception countries.
       *
       * The "exceptions" piled up over and over until it became clear that Hyperwallet simply
       * does not have a working solution for omiting fields for previously entered data. The
       * validation is different in many ways, or sometimes entirely new fields are required
       * based on country, account type, etc.
       *
       * DO NOT skipProfileFields unless you're certain Hyperwallet has fixed these issues
       * (and don't hold your breath)
       */
      if (SPECIAL_COUNTRIES.USE_PROFILE.includes(country)) {
        /**
         * Not sure why we're explicitly not pre-filling data we already have for these countries. I'm guessing
         * we saw some problems with the data not being correct for this step.
         */
        options.skipProfileFields = false;
      } else {
        options.skipProfileFields = false;
        options.data = {
          firstName: hyperwalletUser.get('firstName'),
          lastName: hyperwalletUser.get('lastName'),
          addressLine1: hyperwalletUser.get('addressLine1'),
          addressLine2: hyperwalletUser.get('addressLine2'),
          city: hyperwalletUser.get('city'),
          country: hyperwalletUser.get('country'),
          postalCode: hyperwalletUser.get('postalCode'),
          stateProvince: hyperwalletUser.get('stateProvince'),
          dateOfBirth: hyperwalletUser.get('dateOfBirth'),
          businessName: hyperwalletUser.get('businessName'),
        };
      }

      widget = HWWidgets.transferMethods.configure(options);

      widget.display();
    };

    const hyperwalletScript = document.createElement('script');

    hyperwalletScript.onload = () => {
      if (this.get('isScriptRemoved')) return;

      HWWidgets.initialize((onSuccess) => {
        retrieveAuthenticationToken((authToken) => {
          onSuccess(authToken);
        });
      });

      configureWidget();
    };

    hyperwalletScript.onerror = () => {
      if (this.get('isScriptRemoved')) return;

      this.set('isLoading', false);

      showModal(this, 'confirmation', {
        titleText: 'An error occurred',
        bodyText: 'Please try again later.',
        actionButtonText: 'Ok',
      });
    };

    const host = this.get('scriptHost');
    const token = this.get('hyperwalletUser.token');
    hyperwalletScript.src = `https://${host}/rest/widgets/transfer-methods/${token}/en.min.js`;

    document.head.appendChild(hyperwalletScript);

    this.set('removeScript', () => {
      hyperwalletScript.remove();
      this.set('isScriptRemoved', true);
    });
  },

  willDestroyElement() {
    this._super(...arguments);

    this.get('removeScript')();
  },
});
